import { apiClient } from '@/services/api-client';
import { secureStorage } from './secure-storage';
import type {
  AdminUser,
  LoginCredentials,
  AuthToken,
  UserSession,
  UserRole,
} from '@/types';

// --- Constants ---

const SESSION_KEY = 'xs_admin_session';
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const TOKEN_REFRESH_BUFFER_MS = 60 * 1000; // refresh 1 min before expiry
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'keydown', 'scroll', 'click'];

// --- Auth Service Interface ---

export interface AuthService {
  login(credentials: LoginCredentials): Promise<AuthToken>;
  loginWithEntraId(params: {
    code: string;
    codeVerifier: string;
    nonce: string;
    redirectUri: string;
  }): Promise<AuthToken>;
  logout(): Promise<{ authProvider?: string }>;
  refreshToken(token: string): Promise<AuthToken>;
  validateToken(token: string): Promise<boolean>;
  getSession(): UserSession | null;
  setSession(session: UserSession): void;
  clearSession(): void;
  getCurrentUser(): AdminUser | null;
  getUserRole(): UserRole;
  startActivityTracking(): void;
  stopActivityTracking(): void;
  onSessionExpired(callback: () => void): void;
}

// --- Implementation ---

class AuthServiceImpl implements AuthService {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionExpiredCallback: (() => void) | null = null;
  private boundActivityHandler: (() => void) | null = null;

  async login(credentials: LoginCredentials): Promise<AuthToken> {
    const response = await apiClient.post<{
      user: AdminUser;
      token: AuthToken;
      authProvider?: string;
    }>('/auth/login', credentials);

    const { user, token, authProvider } = response.data;

    const session: UserSession = {
      user,
      token,
      expiresAt: Date.now() + token.expiresIn * 1000,
      lastActivity: Date.now(),
      authProvider: (authProvider as UserSession['authProvider']) ?? 'local',
    };

    this.setSession(session);
    this.startActivityTracking();
    return token;
  }

  /**
   * Complete the Entra ID login flow by exchanging the authorization code
   * for a platform session via the backend.
   *
   * Requirements: 2.5, 2.6, 6.1, 6.2, 6.3, 6.4, 7.4
   */
  async loginWithEntraId(params: {
    code: string;
    codeVerifier: string;
    nonce: string;
    redirectUri: string;
  }): Promise<AuthToken> {
    const response = await apiClient.post<{
      user: AdminUser;
      token: AuthToken;
      authProvider: 'entra_id';
    }>('/auth/entra/callback', params);

    const { user, token, authProvider } = response.data;

    const session: UserSession = {
      user,
      token,
      expiresAt: Date.now() + token.expiresIn * 1000,
      lastActivity: Date.now(),
      authProvider,
    };

    this.setSession(session);
    this.scheduleTokenRefresh(token);
    this.startActivityTracking();

    return token;
  }

  async logout(): Promise<{ authProvider?: string }> {
    const session = this.getSession();
    const authProvider = session?.authProvider;

    this.stopActivityTracking();
    this.clearRefreshTimer();

    if (session) {
      try {
        await apiClient.post('/auth/logout', null, {
          headers: { Authorization: `Bearer ${session.token.accessToken}` },
        });
      } catch {
        // Best-effort revocation — clear local state regardless
      }
    }

    this.clearSession();
    return { authProvider };
  }

  async refreshToken(token: string): Promise<AuthToken> {
    const response = await apiClient.post<AuthToken>('/auth/refresh', {
      refreshToken: token,
    });
    const newToken = response.data;

    const session = this.getSession();
    if (session) {
      const updated: UserSession = {
        ...session,
        token: newToken,
        expiresAt: Date.now() + newToken.expiresIn * 1000,
      };
      this.setSession(updated);
      this.scheduleTokenRefresh(newToken);
    }

    return newToken;
  }

  async validateToken(token: string): Promise<boolean> {
    try {
      const response = await apiClient.post<{ valid: boolean }>('/auth/validate', {
        token,
      });
      return response.data.valid;
    } catch {
      return false;
    }
  }

  getSession(): UserSession | null {
    const raw = secureStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    try {
      const session: UserSession = JSON.parse(raw);

      // Check if session has expired
      if (Date.now() > session.expiresAt) {
        this.clearSession();
        return null;
      }

      // Check inactivity timeout
      if (Date.now() - session.lastActivity > INACTIVITY_TIMEOUT_MS) {
        this.clearSession();
        this.sessionExpiredCallback?.();
        return null;
      }

      return session;
    } catch {
      this.clearSession();
      return null;
    }
  }

  setSession(session: UserSession): void {
    secureStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  clearSession(): void {
    secureStorage.removeItem(SESSION_KEY);
    this.clearRefreshTimer();
  }

  getCurrentUser(): AdminUser | null {
    return this.getSession()?.user ?? null;
  }

  getUserRole(): UserRole {
    return this.getSession()?.user.role ?? 'viewer';
  }

  onSessionExpired(callback: () => void): void {
    this.sessionExpiredCallback = callback;
  }

  startActivityTracking(): void {
    this.stopActivityTracking();

    this.boundActivityHandler = () => this.handleActivity();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, this.boundActivityHandler, { passive: true });
    }

    this.resetInactivityTimer();
  }

  stopActivityTracking(): void {
    if (this.boundActivityHandler) {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, this.boundActivityHandler);
      }
      this.boundActivityHandler = null;
    }

    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  // --- Private helpers ---

  private handleActivity(): void {
    const session = this.getSession();
    if (session) {
      const updated: UserSession = { ...session, lastActivity: Date.now() };
      this.setSession(updated);
    }
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }

    this.inactivityTimer = setTimeout(() => {
      this.clearSession();
      this.stopActivityTracking();
      this.sessionExpiredCallback?.();
    }, INACTIVITY_TIMEOUT_MS);
  }

  private scheduleTokenRefresh(token: AuthToken): void {
    this.clearRefreshTimer();

    const refreshIn = token.expiresIn * 1000 - TOKEN_REFRESH_BUFFER_MS;
    if (refreshIn <= 0) return;

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshToken(token.refreshToken);
      } catch {
        // Refresh failed — session will expire naturally
        this.clearSession();
        this.stopActivityTracking();
        this.sessionExpiredCallback?.();
      }
    }, refreshIn);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

// Singleton export
export const authService: AuthService = new AuthServiceImpl();
